import React, { useState, useEffect, useContext } from "react";
import classNames from "classnames";
import styles from "./TextColorEdit.scss";
import { RichUtils, Modifier, EditorState } from "draft-js";
import { rgbStringToHex } from "../../../../../utils/colorUtils";
import { Row } from "../../../../grid/Row";
import { Col } from "../../../../grid/Col";
import { Icon } from "../../../../graphic";
import { MarkupEditorContext } from "../../MarkupEditorContext";

export const TextColorEdit = ({ editorState, onChange, setEditingEntity }) => {
    const { textColors } = useContext(MarkupEditorContext);
    const [foregroundColor, setForegroundColor] = useState(null);
    const [backgroundColor, setBackgroundColor] = useState(null);

    useEffect(() => {
        const currentStyle = editorState.getCurrentInlineStyle();
        currentStyle.forEach((key) => {
            if (key.indexOf("bgcolor-") === 0) {
                const colorValue = key.substring(8);
                setBackgroundColor(colorValue.startsWith("rgb") ? rgbStringToHex(colorValue) : colorValue);
            } else if (key.indexOf("color-") === 0) {
                const colorValue = key.substring(6);
                setForegroundColor(colorValue.startsWith("rgb") ? rgbStringToHex(colorValue) : colorValue);
            }
        });
    }, [editorState]);

    const handleRemoveColors = (param) => {
        const selection = editorState.getSelection();
        const currentStyle = editorState.getCurrentInlineStyle();

        // Remove existing colors
        const nextContentState = currentStyle.reduce((contentState, styleValue) => {
            if (styleValue.indexOf(param) === 0) {
                return Modifier.removeInlineStyle(contentState, selection, styleValue);
            } else {
                return contentState;
            }
        }, editorState.getCurrentContent());

        // Push inline style changes
        let nextEditorState = EditorState.push(editorState, nextContentState, "change-inline-style");

        onChange(nextEditorState);
    };

    const handleSelectColor = (param, newColor) => {
        handleRemoveColors(param);
        let nextEditorState;

        const colorString = `${param}-${newColor}`;
        const currentStyle = editorState.getCurrentInlineStyle();

        // If the color is being toggled on, apply it.
        if (!currentStyle.has(colorString)) {
            nextEditorState = RichUtils.toggleInlineStyle(editorState, colorString);
        }

        onChange(nextEditorState);
        setEditingEntity(null);
    };

    return (
        <div className={styles.base} data-width="302">
            {/* Foregorund color */}
            {textColors?.foreground ? (
                <Row gutter={["small", "small"]}>
                    <Col span={24}>
                        <div className={styles.label}>Tekstfarge</div>
                    </Col>
                    <Col span={24}>
                        <ColorPicker
                            param="color"
                            colors={textColors?.foreground}
                            activeColor={foregroundColor}
                            setActiveColor={setForegroundColor}
                            handleSelectColor={handleSelectColor}
                            handleRemoveColors={handleRemoveColors}
                        />
                    </Col>
                </Row>
            ) : null}

            {/* Background color */}
            {textColors?.background ? (
                <Row gutter={["small", "small"]}>
                    <Col span={24}>
                        <div className={styles.label}>Bakgrunnsfarge</div>
                    </Col>
                    <Col span={24}>
                        <ColorPicker
                            param="bgcolor"
                            colors={textColors?.background}
                            activeColor={backgroundColor}
                            setActiveColor={setBackgroundColor}
                            handleSelectColor={handleSelectColor}
                            handleRemoveColors={handleRemoveColors}
                        />
                    </Col>
                </Row>
            ) : null}
        </div>
    );
};

const ColorPicker = ({ param, colors, activeColor, setActiveColor, handleSelectColor, handleRemoveColors }) => {

    const onMouseDown = (event, {param, color}) => {
        event.preventDefault();
        setActiveColor(color);
        handleSelectColor(param, color);
    };

    return (
        <div className={styles.buttons}>
            {colors.map((color) => (
                <button
                    key={color}
                    className={classNames(styles.button, {
                        [styles.active]: activeColor === color
                    })}
                    onMouseDown={event => onMouseDown(event, {param, color})}
                >
                    <div className={styles.color} style={{ backgroundColor: color }}></div>
                </button>
            ))}
            <button
                className={classNames(styles.button, styles.clear)}
                onClick={() => setActiveColor(null) | handleRemoveColors(param)}
            >
                <div className={styles.color}>
                    <Icon name="forbidden" />
                </div>
            </button>
        </div>
    );
};
